import React, {ButtonHTMLAttributes} from 'react';
import {Styles} from 'config';
import LoadingSpinner from './LoadingSpinner';

type ButtonVariantType =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'warningPrimary'
  | 'warningSecondary';
type ButtonSizeType = 'normal' | 'small';
type StateType = Record<'active' | 'disabled' | 'loading', PropType>;
type PropType = {
  backgroundColor: string;
  borderColor: string;
  color: string;
};

const COLORS_MAP: Record<ButtonVariantType, StateType> = {
  primary: {
    active: {
      backgroundColor: Styles.colors.primary,
      borderColor: Styles.colors.primary,
      color: '#FFFFFF',
    },
    disabled: {
      backgroundColor: Styles.colors.disabled,
      borderColor: Styles.colors.disabled,
      color: '#FFFFFF',
    },
    loading: {
      backgroundColor: Styles.colors.disabled,
      borderColor: Styles.colors.disabled,
      color: '#FFFFFF',
    },
  },
  secondary: {
    active: {
      backgroundColor: '#FFFFFF',
      borderColor: Styles.colors.primary,
      color: Styles.colors.primary,
    },
    disabled: {
      backgroundColor: Styles.colors.disabled,
      borderColor: Styles.colors.disabled,
      color: '#FFFFFF',
    },
    loading: {
      backgroundColor: Styles.colors.disabled,
      borderColor: Styles.colors.disabled,
      color: '#FFFFFF',
    },
  },
  tertiary: {
    active: {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      color: Styles.colors.primary,
    },
    disabled: {
      backgroundColor: Styles.colors.disabled,
      borderColor: Styles.colors.disabled,
      color: '#FFFFFF',
    },
    loading: {
      backgroundColor: Styles.colors.disabled,
      borderColor: Styles.colors.disabled,
      color: '#FFFFFF',
    },
  },
  warningPrimary: {
    active: {
      backgroundColor: Styles.colors.warningRed.normal,
      borderColor: Styles.colors.warningRed.normal,
      color: Styles.colors.white,
    },
    disabled: {
      backgroundColor: Styles.colors.disabled,
      borderColor: Styles.colors.disabled,
      color: '#FFFFFF',
    },
    loading: {
      backgroundColor: Styles.colors.disabled,
      borderColor: Styles.colors.disabled,
      color: '#FFFFFF',
    },
  },
  warningSecondary: {
    active: {
      backgroundColor: '#FFFFFF',
      borderColor: Styles.colors.warningRed.normal,
      color: Styles.colors.warningRed.normal,
    },
    disabled: {
      backgroundColor: Styles.colors.disabled,
      borderColor: Styles.colors.disabled,
      color: '#FFFFFF',
    },
    loading: {
      backgroundColor: Styles.colors.disabled,
      borderColor: Styles.colors.disabled,
      color: '#FFFFFF',
    },
  },
};

interface IButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  text: string;
  variant?: ButtonVariantType;
  size?: ButtonSizeType;
  loading?: boolean;
}

const Button: React.FC<IButtonProps> = props => {
  const {text, variant = 'primary', disabled = false, loading = false, ...rest} = props;
  const state: keyof StateType = loading ? 'loading' : disabled ? 'disabled' : 'active';

  const backgroundColor = COLORS_MAP[variant][state].backgroundColor;
  const borderColor = COLORS_MAP[variant][state].borderColor;
  const color = COLORS_MAP[variant][state].color;
  const textStyle = {opacity: loading ? 0 : 1, color};

  return (
    <button
      {...rest}
      type="button"
      className={`flex justify-center items-center w-full rounded-md font-medium border h-[3rem] transition duration-200 ${
        disabled ? 'hover:opacity-100' : 'hover:opacity-75'
      }`}
      style={{backgroundColor, borderColor, color}}
      disabled={loading || disabled}>
      <p style={textStyle}>{text}</p>
      {loading && <LoadingSpinner />}
    </button>
  );
};

export default Button;
